import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable, throwError } from "rxjs";

import { APP_CONFIG } from "@application/config";

import {
  BiometricReader,
  EnrolledTemplate,
  FingerPosition,
  TemplateRaw,
} from "../entities/profile-biometrics.model";

interface FingerprintTemplateDto {
  FingerprintTemplateId: string;
  FingerPosition: FingerPosition;
}

const EDGE_GET_READERS_PATH = (edgeId: string): string =>
  `/v1/edge-command/${edgeId}/get-biometric-readers`;

const EDGE_CAPTURE_PATH = (edgeId: string): string =>
  `/v1/edge-command/${edgeId}/capture-biometric`;

const FINGERPRINT_IS_VALID_PATH = "/Neurotechnology/IsValidTemplate";
const FINGERPRINT_VERIFY_MATCHED_PATH = "/Neurotechnology/VerifyMatched";
// Único endpoint REST que respeita os verbos HTTP: POST (add), GET (list),
// DELETE (remove). Não existem rotas /FingerprintTemplate/Add ou /Remove.
const FINGERPRINT_TEMPLATE_PATH = "/FingerprintTemplate";

@Injectable({ providedIn: "root" })
export class ProfileBiometricsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;

  public getBiometricReaders(
    edgeId: string,
  ): Observable<BiometricReader[]> {
    const url =
      this.apiUrl.access_edge_manager_service +
      EDGE_GET_READERS_PATH(edgeId);

    return this.http.post<BiometricReader[]>(url, {}, { withCredentials: true });
  }

  public captureBiometric(edgeId: string): Observable<TemplateRaw> {
    const url =
      this.apiUrl.access_edge_manager_service + EDGE_CAPTURE_PATH(edgeId);
    return this.http
      .post(url, {}, { responseType: "text", withCredentials: true })
      .pipe(
        map((response) => {
          const template = this.unwrapJsonString(response?.trim() ?? "");
          if (!template) {
            throw new Error("capture-biometric returned empty response");
          }
          return template;
        }),
      );
  }

  private unwrapJsonString(value: string): string {
    if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed === "string") return parsed;
      } catch {
        // não é JSON-encoded válido — devolve raw com as aspas externas removidas
        return value.slice(1, -1);
      }
    }
    return value;
  }

  public isValidTemplate(templateRaw: TemplateRaw): Observable<boolean> {
    return this.http.post<boolean>(
      this.apiUrl.fingerprint_service + FINGERPRINT_IS_VALID_PATH,
      { templateRaw },
      { withCredentials: true },
    );
  }

  public verifyMatched(
    probeTemplateRaw: TemplateRaw,
    candidateTemplateRaw: TemplateRaw,
  ): Observable<boolean> {
    return this.http.post<boolean>(
      this.apiUrl.fingerprint_service + FINGERPRINT_VERIFY_MATCHED_PATH,
      { probeTemplateRaw, candidateTemplateRaw },
      { withCredentials: true },
    );
  }

  public addTemplate(
    personId: string,
    templatesRaw: TemplateRaw[],
    finger: FingerPosition,
  ): Observable<EnrolledTemplate | null> {
    return this.http
      .post<FingerprintTemplateDto | null>(
        this.apiUrl.fingerprint_service + FINGERPRINT_TEMPLATE_PATH,
        {
          PersonId: personId,
          TemplatesRaw: templatesRaw,
          FingerPosition: finger,
        },
        { withCredentials: true },
      )
      .pipe(map((row) => (row ? this.toEnrolledTemplate(row) : null)));
  }

  public listTemplatesForPerson(
    personId: string,
  ): Observable<EnrolledTemplate[]> {
    if (!personId) {
      return throwError(() => new Error("Missing personId"));
    }

    const params = new HttpParams().set("PersonId", personId);

    return this.http
      .get<FingerprintTemplateDto[]>(
        this.apiUrl.fingerprint_service + FINGERPRINT_TEMPLATE_PATH,
        { params, withCredentials: true },
      )
      .pipe(map((rows) => rows.map((row) => this.toEnrolledTemplate(row))));
  }

  public removeTemplate(templateId: string): Observable<void> {
    const params = new HttpParams().set("FingerprintTemplateId", templateId);

    return this.http.delete<void>(
      this.apiUrl.fingerprint_service + FINGERPRINT_TEMPLATE_PATH,
      { params, withCredentials: true },
    );
  }

  private toEnrolledTemplate(row: FingerprintTemplateDto): EnrolledTemplate {
    return { templateId: row.FingerprintTemplateId, finger: row.FingerPosition };
  }
}
